import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000, // 30s default safety timeout
});

// ---- helpers ----
function normalizeAndThrow(err, fallback = "Request failed") {
  const status = err?.response?.status;

  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;

  const e = new Error(detail);
  e.status = status;
  e.data = err?.response?.data;

  throw e;
}

/**
 * Generate cover letter
 */
export async function generateCoverLetter({
  optimizedResume,
  jobDescription,
  tone = "professional",
  email,
}) {
  if (!email) {
    throw new Error("Missing user email for cover letter generation.");
  }

  if (!optimizedResume || !jobDescription) {
    throw new Error("Optimised resume and job description are required.");
  }

  try {
    const { data } = await api.post("/generate_cover_letter", {
      optimized_resume: optimizedResume,
      job_description: jobDescription,
      tone,
      email,
    });

    return data.cover_letter;
  } catch (err) {
    normalizeAndThrow(err, "Failed to generate cover letter");
  }
}

/**
 * Generate Interview Pack
 */
export async function generateInterviewPack({
  email,
  optimizedResume,
  jobDescription,
  role = null,
  company = null,
  difficulty = "Medium",
  count = 3,
  categories = {
    behavioral: true,
    technical: true,
    role_specific: true,
  },
}) {
  if (!email) {
    throw new Error("Missing user email for interview pack.");
  }

  if (!optimizedResume || !jobDescription) {
    throw new Error("Optimised resume and job description are required.");
  }

  try {
    const { data } = await api.post(
      "/interview_prep",
      {
        email,
        optimized_resume: optimizedResume,
        job_description: jobDescription,
        role,
        company,
        difficulty,
        count,
        categories,
      },
      {
        timeout: 90000, // 90s for heavier Vertex AI interview generation
      }
    );

    return data;
  } catch (err) {
    normalizeAndThrow(err, "Failed to generate interview pack");
  }
}

export async function recommendJobsFromCv({ file, targetRole = "", location = "London" }) {
  if (!file) {
    throw new Error("Missing CV file.");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("target_role", targetRole);
  formData.append("location", location);

  try {
    const { data } = await api.post("/jobs/recommend", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 90000,
    });

    return data;
  } catch (err) {
    normalizeAndThrow(err, "Failed to recommend jobs");
  }
}

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line no-console
  console.log("API base URL:", BASE_URL);
}
